import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronLeft, X, Star, Sun, Meh, CloudRain, Frown, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { calculateDuration, calculateScore, type SleepQuality } from "@/hooks/useSleep";

const ACCENT = "#00e5ff";

const QUALITY_OPTIONS: { value: SleepQuality; icon: React.ElementType; color: string; key: string }[] = [
  { value: "amazing", icon: Star, color: ACCENT, key: "sleep.quality.amazing" },
  { value: "good", icon: Sun, color: "#14b8a6", key: "sleep.quality.good" },
  { value: "fair", icon: Meh, color: "#6b7280", key: "sleep.quality.fair" },
  { value: "poor", icon: CloudRain, color: "#f59e0b", key: "sleep.quality.poor" },
  { value: "terrible", icon: Frown, color: "#ef4444", key: "sleep.quality.terrible" },
];

function formatDuration(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${String(m).padStart(2, "0")}min`;
}

export interface SleepLogSheetProps {
  open: boolean;
  onClose: () => void;
  onSave: (payload: {
    bedtime: string;
    wakeup: string;
    quality: SleepQuality;
  }) => Promise<void>;
  targetHours: number;
  initialTimes?: { bedtime: string; wakeup: string };
}

export const SleepLogSheet: React.FC<SleepLogSheetProps> = ({ open, onClose, onSave, targetHours, initialTimes }) => {
  const { t } = useLanguage();
  const reducedMotion = useReducedMotion();
  const busy = useRef(false);
  const dialog = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); if (!busy.current) onCloseRef.current(); }
      if (event.key !== "Tab") return;
      const fields = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),[tabindex="0"]') ?? []);
      const first = fields[0], last = fields[fields.length - 1];
      if (!first) { event.preventDefault(); dialog.current?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keyboard);
    return () => { document.removeEventListener("keydown", keyboard); if (previous?.isConnected) previous.focus(); };
  }, [open]);
  const [error, setError] = useState(false);
  // 2-step wizard: 1 = unified bedtime+wakeup, 2 = quality (auto-saves).
  const [step, setStep] = useState<1 | 2>(1);
  const [bedtime, setBedtime] = useState(initialTimes?.bedtime ?? "23:00");
  const [wakeup, setWakeup] = useState(initialTimes?.wakeup ?? "07:00");
  const [quality, setQuality] = useState<SleepQuality | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1); setBedtime(initialTimes?.bedtime ?? "23:00"); setWakeup(initialTimes?.wakeup ?? "07:00");
      setQuality(null); setSaving(false); setError(false); busy.current = false;
    }
  }, [open, initialTimes]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const duration = calculateDuration(bedtime, wakeup);
  const valid = Number.isFinite(duration) && duration > 0 && duration < 24;
  const estimatedScore = valid ? calculateScore(duration, targetHours) : 0;

  const handleBack = useCallback(() => {
    if (busy.current) return;
    if (step === 1) {
      onClose();
    } else {
      setStep(1);
    }
  }, [step, onClose]);

  const handleNext = useCallback(() => {
    if (step === 1 && valid) setStep(2);
  }, [step, valid]);

  const handleQualitySelect = useCallback(
    async (q: SleepQuality) => {
      if (busy.current || !valid) return;
      busy.current = true; setQuality(q); setSaving(true); setError(false);
      try { await onSave({ bedtime, wakeup, quality: q }); }
      catch (error) { console.error("[SleepLogSheet] save failed", error); setError(true); }
      finally { busy.current = false; setSaving(false); }
    }, [bedtime, wakeup, onSave, valid],
  );
  const close = () => { if (!busy.current) onClose(); };

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0"
      style={{ zIndex: 70, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)" }}
      onClick={close}
    >
      <motion.div
        initial={{ y: reducedMotion ? 0 : "100%" }}
        animate={{ y: 0 }}
        exit={{ y: reducedMotion ? 0 : "100%" }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-label={t("sleep.log.title")}
        onClick={(e) => e.stopPropagation()}
        className="fixed bottom-0 flex flex-col overflow-hidden h-sleep-sheet"
        style={{
          // Horizontal centering via auto margins + left/right 0. We
          // intentionally avoid `left: 50%; transform: translateX(-50%)`
          // because framer-motion's `y` animation sets `transform`
          // directly on this element, which overrides Tailwind's compound
          // `-translate-x-1/2` and leaves the sheet shifted right by half
          // its width on every viewport (Rafa 25/04 iPhone + Edge).
          left: 0,
          right: 0,
          marginInline: "auto",
          width: "100vw",
          maxWidth: "min(100vw, 480px)",
          background: "linear-gradient(180deg, hsl(220 35% 9%), hsl(222 38% 7%))",
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          border: "1px solid hsl(220 50% 35% / 0.25)",
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full" style={{ background: "hsl(220 50% 40% / 0.45)" }} />
        </div>

        {/* Top bar: back + progress dots + close */}
        <div className="flex items-center justify-between px-5 py-2">
          <button
            onClick={handleBack}
            className="w-8 h-8 rounded-full flex items-center justify-center active:opacity-70"
            aria-label={t("common.back")} disabled={saving}
          >
            <ChevronLeft size={18} style={{ color: "hsl(218 14% 65%)" }} />
          </button>
          <div className="flex items-center gap-2">
            {[1, 2].map((s) => (
              <div
                key={s}
                className="rounded-full transition-all"
                style={{
                  width: step === s ? 20 : 6,
                  height: 6,
                  background: step >= (s as 1 | 2) ? ACCENT : "hsl(220 50% 30% / 0.45)",
                  boxShadow: step === s ? `0 0 10px ${ACCENT}70` : "none",
                }}
              />
            ))}
          </div>
          <button
            onClick={close}
            className="w-8 h-8 rounded-full flex items-center justify-center active:opacity-70"
            aria-label={t("common.close")} disabled={saving}
          >
            <X size={18} style={{ color: "hsl(218 14% 65%)" }} />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 flex flex-col overflow-y-auto px-5 pt-4"
          style={{
            // Respect the iOS home-indicator safe area so the Next button
            // on step 1/2 isn't visually cut off on iPhone Safari. Keeps
            // the original 24px breathing room on devices with no inset.
            paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)",
          }}
        >
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col min-h-fit"
              >
                <p className="text-[10px] font-display tracking-[0.22em] uppercase mb-1" style={{ color: `${ACCENT}AA` }}>
                  1 / 2
                </p>
                <h2 className="font-display text-[24px] font-bold text-white leading-tight mb-3">
                  {t("sleep.sheet.timesQuestion")}
                </h2>
                {/* Live duration + score readout, moved up from the old
                    step 2 so users see it react to either wheel adjusting. */}
                <div
                  className="rounded-xl px-3 py-2 mb-6 text-center"
                  style={{
                    background: "hsl(220 50% 12% / 0.50)",
                    border: `1px solid ${ACCENT}30`,
                  }}
                >
                  <p className="font-display text-[14px] font-semibold text-white">
                    {valid ? formatDuration(duration) : "—"}{" "}
                    <span style={{ color: "hsl(218 14% 55%)" }}>· {t("sleep.sheet.estimatedScore")} {estimatedScore}</span>
                  </p>
                </div>
                <div className="flex flex-col gap-4 flex-1 mb-4">
                  <label className="block text-sm text-foreground">
                    {t("sleep.bedtime")}
                    <input type="time" value={bedtime} onChange={e => setBedtime(e.target.value)} className="block w-full min-h-12 mt-2 rounded-xl bg-muted px-4 text-lg" />
                  </label>
                  <label className="block text-sm text-foreground">
                    {t("sleep.wakeUp")}
                    <input type="time" value={wakeup} onChange={e => setWakeup(e.target.value)} className="block w-full min-h-12 mt-2 rounded-xl bg-muted px-4 text-lg" />
                  </label>
                  {!valid && <p role="alert" className="text-sm text-amber-300">{t("sleep.invalidTimes")}</p>}
                </div>
                <button
                  onClick={handleNext} disabled={!valid}
                  className="w-full py-4 rounded-2xl font-display text-[16px] font-bold text-white relative overflow-hidden active:scale-[0.97]"
                  style={{
                    background: `linear-gradient(135deg, ${ACCENT}, hsl(200 80% 35%))`,
                    boxShadow: `0 0 32px ${ACCENT}40, inset 0 0 20px ${ACCENT}10`,
                  }}
                >
                  {t("sleep.sheet.next")}
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col min-h-fit"
              >
                <p className="text-[10px] font-display tracking-[0.22em] uppercase mb-1" style={{ color: `${ACCENT}AA` }}>
                  2 / 2
                </p>
                <h2 className="font-display text-[24px] font-bold text-white leading-tight mb-8">
                  {t("sleep.sheet.qualityQuestion")}
                </h2>
                <p className="text-sm text-muted-foreground mb-4">{t("sleep.selectToSave")}</p>
                {error && <p role="alert" className="text-sm text-amber-300 mb-4">{t("sleep.saveError")}</p>}
                <div className="grid grid-cols-5 gap-2 flex-1 place-items-start">
                  {QUALITY_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const selected = quality === opt.value;
                    const showSpinner = saving && selected;
                    return (
                      <motion.button
                        key={opt.value}
                        onClick={() => handleQualitySelect(opt.value)}
                        whileTap={{ scale: 0.92 }}
                        animate={selected && !reducedMotion ? { scale: [1, 1.15, 1.05] } : { scale: 1 }}
                        transition={{ duration: 0.28 }}
                        className="flex flex-col items-center gap-1.5"
                        disabled={saving} aria-pressed={selected}
                      >
                        <div
                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center transition-all relative"
                          style={{
                            background: selected
                              ? `linear-gradient(135deg, ${opt.color}30, ${opt.color}14)`
                              : `${opt.color}10`,
                            border: `2px solid ${selected ? opt.color : `${opt.color}28`}`,
                            boxShadow: selected
                              ? `0 0 22px ${opt.color}50, inset 0 0 12px ${opt.color}20`
                              : "none",
                          }}
                        >
                          <Icon
                            size={28}
                            style={{
                              color: opt.color,
                              opacity: selected ? (showSpinner ? 0.25 : 1) : 0.5,
                              transition: "opacity 0.18s ease",
                            }}
                          />
                          {showSpinner && (
                            <Loader2
                              size={14}
                              className="absolute animate-spin"
                              style={{ color: opt.color }}
                            />
                          )}
                        </div>
                        <span
                          className="text-[10px] font-display font-semibold text-center"
                          style={{ color: selected ? opt.color : "hsl(218 14% 50%)" }}
                        >
                          {t(opt.key)}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SleepLogSheet;
